import type {Option} from 'react-select'

import type {AccountingAccount} from '@pleo-io/deimos'

import type {SupportedLanguage} from '@product-web/i18n'
import type Country from '@product-web/locale/country'
import type {HelpCentreArticle, HelpCentreFolder} from '@product-web/locale/helpers'

export interface TaxCodeRequest {
    name: string
    rate: number
    enabled?: boolean
    type?: 'reverse' | 'normal'
    code?: string
    ingoingTaxAccount?: string
    outgoingTaxAccount?: string
}

export interface TaxCode extends TaxCodeRequest {
    companyId: string
    id: string
    type?: 'reverse' | 'normal'
    ingoingTaxAccount?: string
    outgoingTaxAccount?: string
    createdAt: string
    updatedAt: string
}

export interface TaxCodeOption {
    hidden: boolean
    disabled: boolean
    value: string
    label: string
    groupId?: string
    groupLabel: boolean
}

/**
 * When updating the AccountingSystem below, make sure to also update
 * features/bill-invoices/backend/invoices.bff.ts
 *
 * It can't be imported due to do-not-use-client-imports-in-server-context rule
 */
export enum AccountingSystem {
    BILLY_DIRECT = 'billy_direct',
    BILLY = 'billy',
    BMD = 'bmd',
    BUSINESSCENTRAL = 'businesscentral',
    CEGIDLOOP = 'cegidloop',
    CUSTOM = 'custom',
    DATEV = 'datev',
    DATEV_DIRECT = 'datev_direct',
    DINERO_DIRECT = 'dinero_direct',
    ECONOMIC = 'economic',
    ELO_DIGITAL_OFFICE = 'elo_digital_office',
    EXACT = 'exact',
    FORTNOX = 'fortnox',
    GENERIC = 'generic',
    HOLDED_DIRECT = 'holded_direct',
    INVENTIO = 'inventio',
    LEXOFFICE = 'lexoffice',
    DYNAMICS_365_FINANCE = 'dynamics_365_finance',
    NETSUITE = 'netsuite',
    NONE = 'none',
    ODOO = 'odoo',
    PROCOUNTOR = 'procountor',
    QUICKBOOKS_DIRECT = 'quickbooks_direct',
    SAGE_ACTIVE = 'sage_active',
    SAGE_FIFTY = 'sage50',
    SAP_BUSINESS_ONE = 'sap_business_one',
    SAP_S4HANA = 'SAP S/4HANA',
    TEST_INTEGRATION = 'test_integration',
    TWINFIELD = 'twinfield',
    UNICONTA_DIRECT = 'uniconta_direct',
    VISMA = 'visma',
    VISMADOTNET = 'vismadotnet',
    XERO = 'xero',
    XLEDGER = 'xledger',
}

export enum SystemName {
    BILLY = 'Billy CSV',
    BILLY_DIRECT = 'Billy Online',
    BMD = 'BMD CSV',
    BUSINESSCENTRAL = 'Microsoft Dynamics 365 Business Central',
    CEGIDLOOP = 'Cegid Loop',
    CUSTOM = 'Custom format',
    DATEV = 'Datev CSV',
    DATEV_DIRECT = 'Datev Unternehmen online',
    DINERO = 'Dinero CSV',
    DINERO_DIRECT = 'Dinero Online',
    ECONOMIC = 'Visma e-conomic',
    ELO_DIGITAL_OFFICE = 'Elo ECM',
    EXACT = 'ExactOnline.nl',
    FORTNOX = 'Fortnox',
    FREEAGENT = 'Freeagent',
    GENERIC = 'Generic format',
    HOLDED = 'Holded CSV',
    HOLDED_DIRECT = 'Holded Online',
    INVENTIO = 'Inventio',
    LEXOFFICE = 'lexoffice',
    DYNAMICS_365_FINANCE = 'Microsoft Dynamics 365 Finance',
    NAVISION = 'Dynamics NAV (UK)',
    NETSUITE = 'NetSuite',
    NETVISOR = 'Netvisor',
    ODOO = 'Odoo',
    PROCOUNTOR = 'Procountor',
    QUICKBOOKS = 'QuickBooks CSV',
    QUICKBOOKS_DIRECT = 'QuickBooks Online',
    SAGE_FIFTY = 'Sage 50 Desktop (UK)',
    SAGE_ONE = 'Sage Accounting (UK)',
    SAGE_TWO_HUNDRED = 'Sage 200cloud (UK)',
    SAGE_ACTIVE = 'Sage Active',
    SAP_BUSINESS_ONE = 'SAP Business One',
    SAP_S4HANA = 'SAP S/4HANA',
    SIE = 'SIE',
    SIE_MARATHON = 'Marathon',
    TIMELOG = 'TimeLog',
    TEST_INTEGRATION = 'Partner Integration',
    TWINFIELD = 'Twinfield',
    UNICONTA = 'Uniconta',
    UNICONTA_DIRECT = 'Uniconta Online',
    VISMA = 'Visma eEkonomi',
    VISMADOTNET = 'Visma.NET',
    XERO = 'Xero',
    XLEDGER = 'Xledger',
}

export enum UnsupportedSystemName {
    SAGE_INTACCT = 'Sage Intacct',
    TRIPLETEX = 'Tripletex',
    SEVENOFFICE = '24SevenOffice',
    VISMA_BUSINESS = 'Visma Business',
    DELTEK_MACONOMY = 'Deltek Maconomy',
    BULLETHQ = 'BulletHQ',
    MICROSOFT_DYNAMICS_AX = 'Microsoft Dynamics AX',
    SEVDESK = 'SevDesk',
    ORACLE_CLOUD_ERP = 'Oracle Cloud ERP',
    IFS_CLOUD = 'IFS Cloud',
    WOLTERS_KLUWER_A3CON = 'Wolters Kluwer A3con',
    ZOHO_BOOKS = 'Zoho Books',
    PE_ACCOUNTING = 'PE Accounting',
    CERTINIA = 'Certinia',
    WORKBOOK = 'Workbook',
    OCTOPUS_PMI = 'Octopus PMI',
    FRESHBOOKS = 'Freshbooks',
    MONITOR_ERP_G5 = 'Monitor ERP G5',
    UNIK_BOLIG = 'UNIK Bolig',
    ADERANT = 'Aderant',
    AQILLA = 'Aqilla',
    HOGIA_OPENBUSINESS = 'Hogia OpenBusiness',
    VISMA_MAMUT = 'Visma Mamut',
    UNIT4_ERP = 'Unit4 ERP',
    MICROSOFT_DYNAMICS_C5 = 'Microsoft Dynamics C5',
    EKOMPLET = 'E-komplet',
    CAPEGO_ACCOUNTING = 'Capego Accounting',
    EREGNSKAB = 'e-Regnskab',
    WEB_REGNSKAB = 'Web-Regnskab',
    EG_XENA = 'EG Xena',
    MENETO = 'Meneto',
    YOURSMINC = 'Yoursminc',
    VISMA_FIVALDI = 'Visma Fivaldi',
    TOCONLINE = 'TOConline',
    SPECTER = 'Specter',
    SETLY = 'Setly',
    WINT = 'Wint',
    XPEDEON = 'Xpedeon',
    MESONIC_WINLINE = 'Mesonic WinLine',
    WINFINANS = 'Winfinans',
    SHOWBIZ_BUDGETING_9 = 'Showbiz Budgeting 9',
    SAIGON = 'Saigon ERP',
    WYNNE_RENTALMAN = 'Wynne RentalMan',
    PRONTO_ERP = 'Pronto ERP',
    PRIORITY_ERP = 'Priority ERP',
    PINEWOOD_DMS = 'Pinewood DMS',
    PHENIX = 'Phenix',
    PENNYLANE = 'pennylane',
    ORDERWISE = 'OrderWise',
    ORACLE_EBUSINESS_SUITE = 'Oracle E-Business Suite',
    OFINDA = 'Ofinda',
    MYGESTION = 'myGESTIÓN',
    MICROSOFT_XAL = 'Microsoft XAL',
    MERLIN = 'Merlin',
    LIBERTY_ACCOUNTS = 'Liberty Accounts',
    KLYANT = 'Klyant',
    KEYLOOP = 'Keyloop',
    ISAMS_IFINANCE = 'iSAMS iFinance',
    HAUFE_X360 = 'Haufe X360',
    GESIO_ERP = 'GESIO ERP',
    GDI_ERP = 'GDI ERP',
    FULLL = 'fulll',
    FORENINGLET = 'ForeningLet',
    FIZEN = 'Fizen',
    FIMOX_SOFTWARE = 'fimox Software',
    DELSOL = 'DELSOL',
    DELSOL_CONTASOL = 'DELSOL CONTASOL',
    DELSOL_REVISO = 'DELSOL REVISO',
    DELSOL_FACTUSOL = 'DELSOL FACTUSOL',
    GEMINI_SYSTEMS = 'Gemini Systems Evolution',
    CSB = 'CSB',
    CEGID_CONTASIMPLE = 'Cegid Contasimple',
    COMARCH_ERP_ENTERPRISE = 'Comarch ERP Enterprise',
    COLLMEX = 'Collmex',
    COINS = 'COINS',
    CDK_GLOBAL = 'CDK Global',
    CAMEO = 'Cameo',
    ECLIPSE = 'Eclipse',
    ABAS_ERP = 'Abas ERP',
    ACD_COMTABILITE_EXPERT = 'ACD Comptabilité Expert',
    MONITOR_ERP_G4 = 'Monitor ERP G4',
    SAP_BUSINESS_BYDESIGN = 'SAP Business ByDesign',
    SAP_R3 = 'SAP R/3',
    SAGE_X3 = 'Sage X3',
    SAGE_DESPACHOS = 'Sage Despachos',
    VISMA_CONTROL_EDGE = 'Visma Control Edge',
    YUKI = 'Visma Yuki',
    VISMA_EACCOUNTING = 'Visma eAccounting',
    UNIK_ADVOSYS = 'UNIK Advosys',
    IRIS_ELEMENTS = 'IRIS Elements',
    PAPRIKA = 'Paprika',
    WOLTERS_KLUWER_ADDISON = 'Wolters Kluwer Addison',
    DIAMANT = 'Diamant',
    SNELSTART_ACCOUNTANT = 'Snelstart Accountant',
    KALIN_SETTERBERG_MARATHON = 'Kalin Setterberg Marathon',
    ADVOPRO = 'Advopro',
    EG_ASPECT4 = 'EG Aspect4',
    WORKDAY_FINANCIAL_MANAGEMENT = 'Workday Financial Management',
    VISMA_SPEEDLEDGER = 'Visma Speedledger',
    POWEROFFICE_GO = 'PowerOffice GO',
    UNI_ECONOMY = 'Uni Economy',
    DUETT_OKONOMI = 'Duett Økonomi',
    DNB_REGNSKAP = 'DNB Regnskap',
    CONTA = 'Conta',
    FIKEN = 'Fiken',
    LIKEIT_ACCOUNTING = 'Likeit Accounting',
    ACCOUNTOR_GO = 'Accountor Go',
    WOLTERS_KLUWER_A3ERP = 'Wolters Kluwer A3ERP',
    AAREON_WODIS_YUNEO = 'Aareon Wodis Yuneo',
    ABACUS_ALLPROJECTS = 'Abacus allprojects',
    ACCESS_FINANCIALS = 'Access Financials',
    ACCONOMY = 'acconomy',
    ACCOUNTSIQ = 'AccountsIQ',
    EASI_ADFINITY = 'easi adfinity',
    OPEN_ACCOUNTING = 'Open Accounting',
    SAGE_GENERATION_EXPERTS = 'Sage Génération Experts',
    AFAS = 'AFAS',
    AGENDA_SOFTWARE = 'Agenda Software',
    AMONDIS_FINANZBUCHHALTUNG = 'Amondis Finanzbuchhaltung',
    ANFIX = 'Anfix',
    BIG_RED = 'Big Red Cloud',
    BILLOMAT = 'Billomat',
    BJORN_LUNDEN = 'Björn Lundén',
    BOKIO = 'Bokio',
    SAGE_BRIGHTPEARL = 'Sage Brightpearl',
    BRIGHTBOOKS = 'BrightBooks',
    WOLTERS_KLUWER_BRILJANT = 'Wolters Kluwer Briljant Account',
    BRZ_365 = 'BRZ 365',
    BUCHHALTUNGS = 'BuchhaltungsButler',
    CANDIS = 'Candis',
    CEGID_QUADRA_ENTREPRISE = 'Cegid Quadra Entreprise',
    CEGID_XRP_FLEX = 'Cegid XRP Flex',
    CEGID_XRP_SPRINT = 'Cegid XRP Sprint',
    CLEARBOOKS = 'ClearBooks',
    DE_FACTO = 'De Facto',
    EGECKO = 'eGecko',
    DIVALTO = 'Divalto',
    DOMONDA = 'Domonda',
    DOOER = 'Dooer',
    EBOUKHOUDENNL = 'e-Boukhouden.nl',
    EG_BOLIG = 'EG Bolig',
    ADVANCED_FINANCIALS = 'Advanced Financials',
    SCOPEVISIO_FIBUNET = 'Scopevisio FibuNet',
    SCOPEVISIO = 'Scopevisio',
    SCOPEVISIO_FILOSOF = 'Scopevisio Filosof',
    FILOSOFT = 'Filosoft',
    GUS_ERP = 'GUS ERP',
    INFOR_M3 = 'Infor M3',
    TEAMSYSTEM_ENTREPRISE_CLOUD = 'TeamSystem Entreprise Cloud',
    TEAMSYSTEM_INDUSTRY = 'TeamSystem Industry',
    TEAMSYSTEM_AZIENDA_CLOUD = 'TeamSystem Azienda Cloud',
    ZUCCHETTI_AD_HOC_REVOLUTION = 'Zucchetti Ad Hoc Revolution',
    ZUCCHETTI_AD_HOC_INFINITY = 'Zucchetti Ad Hoc Infinity',
    ZUCCHETTI_MAGOCLOUD = 'Zucchetti MagoCloud',
    ZUCCHETTI_ONDA = 'Zucchetti Onda',
    SISTEMI_STUDIO = 'Sistemi Studio',
    SISTEMI_SPRING = 'Sistemi Spring',
    SISTEMI_PROFIS = 'Sistemi Profis',
    FLUENTIS_ERP = 'Fluentis ERP',
    TEAMSYSTEM_FATTURE_IN_CLOUD = 'TeamSystem Fatture in Cloud',
    ORACLE_JD_EDWARDS = 'Oracle JD Edwards',
    EPICOR_KINETIC = 'Epicor Kinetic',
    EPICOR_ISCALA = 'Epicor iScala',
    ERPLY_BOOKS = 'Erply Books',
    ACUMATICA = 'Acumatica',
    BLACKLINE = 'Blackline',
    TALLYPRIME = 'TallyPrime',
    SYSPRO = 'SYSPRO',
    RAMCO_ERP = 'Ramco ERP',
    SEGES_INTERCOUNT = 'SEGES InterCount',
    EXACT_GLOBE = 'Exact Globe',
    WECLAPP = 'Weclapp',
    CACTUS_TIMESAG_OG_FINANS = 'Cactus Time-Sag og Finans',
    ORACLE_PEOPLESOFT_FSCM = 'Oracle PeopleSoft FSCM',
    YARDI_VOYAGER_COMMERCIAL = 'Yardi Voyager Commercial',
    VISMA_ACCOUNTVIEW = 'Visma AccountView',
    VISMA_ADMINISTRATION = 'Visma Administration',
    VISMA_NXT = 'Visma NXT',
    PEGASUS_OPERA3 = 'Pegasus Opera 3',
    INFOR_SUNSYSTEMS = 'Infor SunSystems',
    SIMBA = 'Simba',
    LEXWARE = 'LexWare',
    MONEYBIRD = 'Moneybird',
    PROALPHA_ERP = 'ProAlpha ERP',
    ZENEGY_NUMBERS = 'Zenegy Numbers',
    SAGE_1000 = 'Sage 1000',
    SAGE_300 = 'Sage 300',
    SAGE_100 = 'Sage 100',
    SAGE_500 = 'Sage 500',
    KOMIT = 'komit',
    HELIOS_AUTO = 'Helios Auto',
    INTACT_IQ = 'Intact iQ',
    IPLICIT = 'iplicit',
    MICROSOFT_DYNAMICS_GP = 'Microsoft Dynamics GP',
    CEGID_PRIMAVERA = 'Cegid Primavera',
    RZL = 'RZL',
    WAVE = 'Wave',
    EXACT_WINBOOKS = 'Exact WinBooks',
    JEEVES_ERP = 'Jeeves ERP',
    KASHFLOW = 'KashFlow',
    UNIKUM_PYRAMID = 'Unikum Pyramid',
    XEINADIN_BESPOKE_ACCOUNTING = 'Xeinadin Bespoke Accounting',
    IBIZA = 'IBIZA',
    CONCEPT = 'Concept',
    QUICKFILE = 'QuickFile',
    OS_INDSIGT = 'ØS Indsigt',
    PAPIERKRAM = 'Papierkram',
    SESAM_SOFTWARE = 'Sesam Software',
    TEAMSYSTEM_CONTABILITA_IN_CLOUD = 'TeamSystem Contabilità in Cloud',
    TEAMSYSTEM_TERZO_SETTORE_IN_CLOUD = 'TeamSystem Terzo Settore in Cloud',
    TEAMSYSTEM_REVISO = 'TeamSystem Reviso',
    SILVASOFT = 'Silvasoft',
    WOLTERS_KLUWER_ADSOLUT = 'Wolters Kluwer Adsolut',
    EXACT_SYNERGY = 'Exact Synergy',
    EXACT_FINANCIALS = 'Exact Financials',
    EXACT_PROACC = 'Exact ProAcc',
    EXPERTIS_ERP = 'Expertis ERP',
    ADVANCED_OPENACCOUNTS = 'Advanced OpenAccounts',
    XENTRAL_ERP = 'Xentral ERP',
    PHC_CS = 'PHC CS',
    PHC_GO = 'PHC GO',
    OGL_ERP = 'OGL ERP',
    SELECTLINE = 'SelectLine ERP',
    EBP = 'EBP',
    EDISON = 'Edison',
    FINANCE_ONE = 'Finance ONE',
    CATACLOUD = 'Catacloud',
    QUIPU = 'Quipu',
}

export enum ExportFeature {
    POCKET = 'pocket',
    REPRESENTATION = 'representation',
    REVERSE = 'reverse',
    BILL_INVOICE = 'billInvoice',
}

export enum RepresentationFeatureProperty {
    DEDUCTIBLE_COST_ACCOUNT = 'deductibleCostAccount',
    NON_DEDUCTIBLE_ACCOUNT = 'nonDeductibleAccount',
    INTERNAL_DEDUCTIBLE_COST_ACCOUNT = 'internalDeductibleCostAccount',
    INTERNAL_NON_DEDUCTIBLE_ACCOUNT = 'internalNonDeductibleAccount',
}

export enum ReverseFeatureProperty {
    DEFAULT_INGOING_TAX_ACCOUNT = 'defaultIngoingTaxAccount',
}

export enum PocketFeatureProperty {
    EMPLOYEE_CONTRA_ACCOUNT = 'employeeContraAccount',
    EMPLOYEE_CASHBOOK = 'employeeCashbook',
    TRANSITION_ACCOUNT = 'defaultContraAccount',
}

export enum BillInvoiceFeatureProperty {
    FULL_LIST_OF_ACCOUNTS = 'fullListOfAccounts',
    SUPPLIER = 'supplier',
    EXPORTABLE_ONLY_PAID = 'exportableOnlyPaid',
    SHOW_EXPORT_IN_BILLS_DASHBOARD = 'showExportInBillsDashboard',
    EXPORT_CAN_SYNC = 'exportCanSync',
}

export interface FeatureToggleProperty {
    name: string
    required: boolean
    countries?: Country[]
}

type FeatureToggleProperties = FeatureToggleProperty[]

type SupportedFeatures = {
    [key in ExportFeature]?: FeatureToggleProperties
}

type costCenterTagGroup = {
    title: () => string
    description: () => string
    helpArticleUrl?: string
    codeColumn: boolean
    nameColumn: boolean
}

export type AccountingSettingsCostCenterTagGroup = {
    tagGroupId: string
    codeColumnId: string
    nameColumnId: string
}
export type System = AccountingSystem | string

export function isAccountingPreferenceKey(
    preferences: AccountingPreferences,
    key: string,
): key is AccountingPreferenceKey {
    return Object.keys(preferences).includes(key)
}

type AccountingPreferenceKey = keyof AccountingPreferences

export interface AccountingPreferences {
    direct: boolean
    system: System
    defaultContraAccount?: string // Default reimbursement account
    reverse?: boolean
    cashbook?: string
    employeeCashbook?: string
    defaultIngoingTaxAccount?: string
    deductibleCostAccount?: string
    nonDeductibleAccount?: string
    internalDeductibleCostAccount?: string
    internalNonDeductibleAccount?: string
    contraAccount?: string
    employeeContraAccount?: string
    invoicesPaymentContraAccount?: string
    invoicesPaymentJournal?: string
    timelog?: boolean
    verificationSeries?: string | null
    pdfReceipts: boolean
    defaultOutgoingTaxAccount?: string
    bankFeed?: boolean
    error?: string
    exportFormatId?: string | null
    consultantNumber?: number | null
    clientNumber?: number | null
    fiscalYearMonthStart?: number | null
    exportManualTransfers?: boolean
    // Deimos code that shows which systems includeLoads https://github.com/pleo-io/deimos/blob/master/src/lib/services/settings/company/reducers/include-loads-reducer.ts#L7
    includeLoads?: boolean
    // Used to maintain the state of the accounting system, this is maintained by the call to the checks endpoint on the microservice
    accountingSystemSetupIsValid?: boolean
    holdedPocketContactId?: string
    useCarriageReturn: boolean | null
    costCenter1TagGroup?: AccountingSettingsCostCenterTagGroup
    costCenter2TagGroup?: AccountingSettingsCostCenterTagGroup
    booksymbol?: string
    extendedAccountingSystemSettings?: Record<string, string | null>
}

export interface AccountingSystemDetails {
    value: string
    name: string
    csv?: boolean
    accessLevel?: 'format' | 'minimal' | 'read' | 'readwrite'
    contraAccount?: boolean
    employeeContraAccount?: boolean
    invoicesPaymentContraAccount?: boolean
    hidden?: boolean
    cashbook?: boolean
    employeeCashbook?: boolean
    direct?: boolean
    custom?: boolean
    reverse?: boolean
    reverseWithCode?: boolean
    doesNotSupportTaxCodes?: boolean
    supportsGenericVat?: boolean
    featureFlagged?: boolean
    countries?: Country[]
    noReverse?: boolean
    verificationSeries?: boolean
    hasAccountDefaultTaxCode?: boolean
    consultantNumber?: boolean
    clientNumber?: boolean
    fiscalYear?: boolean
    hasExternalAccountId?: boolean
    hasExternalAccounts?: boolean
    hasCashbookAccounts?: boolean
    useCashbooksOptionsAsEmployeeContraAccount?: boolean
    supportedFeatures?: SupportedFeatures
    directSystemValue?: string
    /** If this integration uses API key authentication */
    apiKeyIntegration?: boolean
    /**
     * Some integrations refer to this as something different, might not be API Key, might be Ref ID or token value.
     * This field allows us to specify the name that we can contextually use in the UI
     */
    apiKeySystemContextName?: string
    /** List of UI elements that specify the instructions on how to get the API key. */
    apiKeyIntegrationHelpInstructions?: JSX.Element[]
    /**
     * Help link that displayed in API key modal footer
     * */
    apiKeyIntegrationHelpFooter?: JSX.Element
    /** Show normal tax table with tax type column */
    normalTaxTableWithType?: boolean | Country[]
    costCenter1TagGroup?: costCenterTagGroup
    costCenter2TagGroup?: costCenterTagGroup
    /** Set to true if this integration supports tags */
    supportsTags?: boolean
    /** This is used in combination with the supportsTags flag. This is the description of tag segments/groups,
     * e.g. Departments and Cost Types OR Classes and Projects, etc.
     */
    tagsPromptDescription?: string
    booksymbol?: boolean
    helpCentre?: {
        /**
         * Collection ID to the Help centre collection that holds the troubleshooting info
         * e.g., '3434763-troubleshooting-xero' points to
         * https://help.pleo.io/en/collections/3434763-troubleshooting-xero
         */
        folderId?: HelpCentreFolder
        /**
         * Article ID to the Help centre that holds the troubleshooting info
         * e.g., '8662142-troubleshooting-guide-to-netsuite' points to
         * https://help.pleo.io/en/articles/8662142-troubleshooting-guide-to-netsuite
         */
        articleId?: HelpCentreArticle
        /**
         * List of languages in which the help centre article is available
         * defaults to en (which can be skipped in the list)
         */
        supportedLanguages?: SupportedLanguage[]
    }
    /** If this integration is an External API */
    isExternalApi?: boolean
    /** If this integration uses the Export Dispatcher */
    isExportDispatcherIntegration?: boolean
    /** Text to go under the system name on the accounting system selection banner */
    subtitle?: string
    /** Configuration of the system residing externally like Netsuite, Test integration */
    externalConfig?: {
        oauthUrl?: string
        configUrl?: string
    }
}

export type AccountingSystemOption = Option & {
    label: string
    value: string
    data: Partial<AccountingSystemDetails>
}

export interface ExportFormat {
    value: string
    name: string
    label: string
    formats: any[]
    subtitle?: string
}

export type ExportFormatOption = Option & {
    name: string
    label: string
    value: string
    subtitle?: string
    data: Partial<ExportFormat>
}

export interface AccountOption {
    label: string
    value: string
    data: AccountingAccount
}

export interface ExternalCompany {
    id: string
    name: string
    selected: boolean
}

export interface ExternalCompanyOption {
    label: string
    value: string
    data: ExternalCompany
}

export enum Flows {
    ONBOARDING = 'onboarding',
    SETTINGS = 'settings',
}
